// Per-chat capture and end-of-run distillation for harness memory.
//
// Capture: `withHarnessMemory` wraps a spec-taking tool (analytics_query)
// so every call lands in a run ledger as a shape plus, for failures, an
// error signature. A failure that matches a lesson already in memory,
// or a fix found earlier in this same chat, gets that lesson appended to
// the error the model sees, which is how the chat stops repeating itself
// mid-run.
//
// Distill: at agent_end, failures are paired with the later success that
// fixed them (harnessMemoryCapture.pairFixes). A cheap task model turns
// the pairs into at most three general rules; when it is unavailable or
// returns nothing usable, a deterministic rule built from the error class
// and the shape diff is stored instead, so learning never depends on the
// model call succeeding. Lessons that were in the prompt and whose error
// recurred anyway are contradicted; lessons whose entity was queried
// without that error are confirmed.

import {
    classifyQueryError,
    describeDiff,
    errorSignature,
    pairFixes,
    specShape,
} from "./harnessMemoryCapture.js";

export const MAX_LESSONS_PER_RUN = 3;
export const MAX_REMEMBER_PER_RUN = 3;
const DISTILL_TIMEOUT_MS = 25_000;

export function createRunLedger() {
    const events = [];
    let distilledUpTo = 0;
    let injected = [];
    let hinted = [];
    return {
        record(ev) {
            events.push({ ...ev, index: events.length, at: new Date().toISOString() });
        },
        events() { return events.slice(); },
        pending() { return events.slice(distilledUpTo); },
        markDistilled() { distilledUpTo = events.length; },
        // Ids of the lessons the current system prompt carries.
        setInjected(ids) { injected = Array.from(new Set(ids || [])); },
        injected() { return injected.slice(); },
        noteHint(entryId, index) { hinted.push({ entryId, index }); },
        hinted() { return hinted.slice(); },
        clearHints() { hinted = []; },
    };
}

function errorTextOf(err) {
    if (err == null) return "";
    if (typeof err === "string") return err;
    return typeof err.message === "string" ? err.message : String(err);
}

// The text appended to a failing tool result when memory has something
// to say about it. Earlier fixes from this same chat come first: they
// are the most specific evidence available.
export function hintFor({ ledger, store, signature, shape }) {
    const lines = [];
    const events = ledger?.events() || [];
    const priorErrors = events.filter((e) => e.kind === "error" && e.signature?.key === signature.key);
    if (priorErrors.length) {
        const { pairs } = pairFixes(events);
        const earlier = pairs.find((p) => p.error.signature?.key === signature.key && p.diff.length);
        if (earlier) {
            lines.push(`Earlier in this chat the same error was fixed by changing: ${describeDiff(earlier.diff)}.`);
        } else {
            lines.push(`This chat has now hit this exact error ${priorErrors.length + 1} times. Change the shape of the query, not just its values, or look the entity up with schema_describe_entity.`);
        }
    }
    const matches = store?.match ? store.match({ signature, shape }) : [];
    const used = [];
    for (const entry of matches.slice(0, 2)) {
        const fix = entry.example?.diff?.length ? ` What worked before: ${describeDiff(entry.example.diff)}.` : "";
        lines.push(`You hit this before (lesson ${entry.id}, confidence ${store.effectiveConfidence(entry).toFixed(2)}): ${entry.rule}${fix}`);
        used.push(entry.id);
    }
    if (!lines.length) return { text: null, used };
    return { text: "\n\nHarness memory:\n" + lines.map((l) => `  - ${l}`).join("\n"), used };
}

// Wrap a tool whose args carry `spec`. The wrapped tool keeps its name,
// description, and schema; only execute changes.
export function withHarnessMemory(tool, { ledger, store, chatId = null }) {
    if (!tool || typeof tool.execute !== "function") return tool;
    return {
        ...tool,
        async execute(toolCallId, args, ...rest) {
            const shape = specShape(args?.spec);
            try {
                const out = await tool.execute(toolCallId, args, ...rest);
                ledger.record({ kind: "ok", tool: tool.name, toolCallId, chatId, shape });
                return out;
            } catch (err) {
                const errorText = errorTextOf(err);
                const signature = errorSignature({ toolName: tool.name, errorText, shape });
                // Look for help before recording this failure, so the
                // repeat count and the earlier-fix search see only what
                // came before it.
                let hint = { text: null, used: [] };
                try { hint = hintFor({ ledger, store, signature, shape }); } catch { /* memory must never mask the real error */ }
                ledger.record({ kind: "error", tool: tool.name, toolCallId, chatId, shape, signature, errorText: signature.template });
                for (const id of hint.used) ledger.noteHint(id, ledger.events().length - 1);
                if (!hint.text) throw err;
                const augmented = err instanceof Error ? err : new Error(errorText);
                try { augmented.message = errorText + hint.text; } catch { /* frozen message: surface the bare error */ }
                throw augmented;
            }
        },
    };
}

const CLASS_RULES = {
    unknown_field: (t) => `That field is not served on this entity (${t}). Check the entity's field list with schema_describe_entity before selecting or filtering on it.`,
    unknown_schema_name: (t) => `That entity or edge name does not exist (${t}). Use the exact names from the schema index.`,
    unknown_enum: (t) => `That enum value does not exist (${t}). Operators, aggregations, and bucket names must be the exact enum names the schema examples use.`,
    row_bound: () => "Record reads on this entity are bounded (200 rows for text search, 1000 otherwise). Lower `limit`, or aggregate with output.metrics instead of listing rows.",
    scan_ceiling: () => "This query scanned past the execution limit. Narrow the time window, filter on an id (endpoint, trace) before searching text, or search a smaller window per call.",
    text_search_needs_term: () => "This entity is a text search view: it needs a LIKE or ILIKE predicate on its search-text field as a top-level conjunct.",
    trace_id_required: () => "Trace-detail entities need a `<ENTITY>_TRACE_ID EQ` predicate; they cannot be scanned by time window alone.",
    time_range: () => "The `timeRange` must be `{ absolute: { start, end } }` in RFC 3339 UTC or `{ relative: { durationSeconds } }`; other shapes and longer windows than the entity allows are rejected.",
    proto_syntax: (t) => `The spec did not parse as the Query protobuf (${t}). Use only the JSON keys the schema examples use: plain \`"value": ["a"]\` for IN, no \`listValue\` / \`stringValue\` wrappers, and \`spec\` as an object, not a string.`,
    validation: (t) => `The planner rejected this shape (${t}).`,
    http_400: (t) => `The API rejected this shape (${t}).`,
};

// A rule with no model in the loop: the class explains the failure. The
// change that made the retry work travels separately as the entry's
// example diff, which the prompt block, the in-chat hint, and /lessons
// each render once.
export function deterministicLesson(pair) {
    const cls = pair.error.signature?.class || "other";
    const template = pair.error.signature?.template || "";
    return CLASS_RULES[cls] ? CLASS_RULES[cls](template) : `This query shape failed (${template}).`;
}

export function pitfallRule(event) {
    const entity = event.shape?.entity ? event.shape.entity.replace("ENTITY_TYPE_", "") : "this entity";
    return `Querying ${entity} with ${event.shape?.timeShape || "no"} window and fields ${(event.shape?.fields || []).slice(0, 4).map((f) => f.replace("DIMENSION_FIELD_", "")).join(", ") || "(none)"} failed: ${event.signature?.template || "unknown error"}. No fix was found in that chat; check the entity's fields and the window rules first.`;
}

// Failures that say nothing about how the API must be called: the
// gateway was down, the caller was not allowed, or the text matched no
// known planner message. A retry that then succeeded is not a lesson.
export const NON_LESSON_CLASSES = new Set(["http_5xx", "http_403", "other"]);

// Candidates the task model sees. Shapes only.
export function distillCandidates(pending) {
    const { pairs: allPairs, unpaired: allUnpaired } = pairFixes(pending);
    const pairs = allPairs.filter((p) => !NON_LESSON_CLASSES.has(p.error.signature?.class));
    const unpaired = allUnpaired.filter((e) => !NON_LESSON_CLASSES.has(e.signature?.class));
    return {
        pairs: pairs.map((p, i) => ({
            id: `c${i}`,
            entity: p.error.shape?.entity || null,
            errorClass: p.error.signature?.class || "other",
            error: p.error.signature?.template || "",
            failingShape: p.error.shape,
            workingShape: p.fix.shape,
            changed: describeDiff(p.diff),
            pair: p,
        })),
        unpaired,
    };
}

export const DISTILL_SYSTEM_PROMPT = [
    "You maintain a small memory of lessons about an analytics query API for a chat agent.",
    "You are given failed queries (as structural shapes: entity, fields, operators, time window kind), the error each produced, and the shape of the later query that succeeded.",
    "Write at most three lessons. A lesson is ONE sentence stating a general rule the agent can apply to a future query: what to do, naming the entity, field, operator, or window rule when it matters. State the rule, do not narrate the incident and do not restate the error text.",
    "Skip a candidate when the failure was about the data (an id that did not exist, an empty result) rather than about how the API must be called.",
    "Return { lessons: [{ candidate_ids, rule, entity, fields }] }. candidate_ids lists the candidate ids the rule covers; entity is the ENTITY_TYPE_ name the rule is specific to or null; fields lists DIMENSION_FIELD_ names the rule depends on, or [].",
].join("\n");

export const DISTILL_OUTPUT_SCHEMA = {
    type: "object",
    properties: {
        lessons: {
            type: "array",
            maxItems: MAX_LESSONS_PER_RUN,
            items: {
                type: "object",
                properties: {
                    candidate_ids: { type: "array", items: { type: "string" } },
                    rule: { type: "string" },
                    entity: { type: ["string", "null"] },
                    fields: { type: "array", items: { type: "string" } },
                },
                required: ["candidate_ids", "rule"],
            },
        },
    },
    required: ["lessons"],
};

function withTimeout(promise, ms) {
    let timer;
    const timeout = new Promise((_, reject) => { timer = setTimeout(() => reject(new Error("distill timed out")), ms); });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

async function modelLessons(candidates, runSubAgent) {
    if (!runSubAgent || !candidates.length) return [];
    const task = JSON.stringify({
        candidates: candidates.map(({ pair, ...c }) => c),
    });
    const out = await withTimeout(runSubAgent({
        role: "task",
        task,
        systemPrompt: DISTILL_SYSTEM_PROMPT,
        tools: [],
        outputSchema: DISTILL_OUTPUT_SCHEMA,
    }), DISTILL_TIMEOUT_MS);
    if (out?.status !== "done") return [];
    const lessons = Array.isArray(out.result?.lessons) ? out.result.lessons : [];
    return lessons
        .filter((l) => l && typeof l.rule === "string" && l.rule.trim())
        .slice(0, MAX_LESSONS_PER_RUN);
}

// Process everything the ledger has recorded since the last distill.
// Returns a summary for logging and tests. Never throws.
export async function distillRun({ ledger, store, chatId = null, runSubAgent = null }) {
    const summary = { stored: [], confirmed: [], contradicted: [], pitfalls: [], modelUsed: false, error: null };
    try {
        const pending = ledger.pending();
        ledger.markDistilled();
        if (!pending.length) return summary;

        const injected = new Set([...ledger.injected(), ...ledger.hinted().map((h) => h.entryId)]);
        ledger.clearHints();
        const recurred = new Set();
        for (const ev of pending) {
            if (ev.kind !== "error") continue;
            for (const entry of store.match({ signature: ev.signature, shape: ev.shape })) {
                if (injected.has(entry.id)) recurred.add(entry.id);
            }
        }
        for (const id of recurred) {
            store.contradict(id, { chatId });
            summary.contradicted.push(id);
        }
        const okEntities = new Set(pending.filter((e) => e.kind === "ok").map((e) => e.shape?.entity).filter(Boolean));
        for (const id of injected) {
            if (recurred.has(id)) continue;
            const entry = store.get(id);
            if (!entry || entry.status !== "active") continue;
            if (!entry.scope?.entity || okEntities.has(entry.scope.entity)) {
                store.confirm(id, { chatId });
                summary.confirmed.push(id);
            }
        }

        const errors = pending.filter((e) => e.kind === "error");
        if (!errors.length) return summary;

        const { pairs: candidates, unpaired } = distillCandidates(pending);
        let lessons = [];
        try {
            lessons = await modelLessons(candidates, runSubAgent);
            summary.modelUsed = lessons.length > 0;
        } catch (err) {
            summary.error = err?.message || String(err);
        }

        const covered = new Set();
        for (const lesson of lessons) {
            const ids = Array.isArray(lesson.candidate_ids) ? lesson.candidate_ids : [];
            const first = candidates.find((c) => ids.includes(c.id)) || null;
            for (const id of ids) covered.add(id);
            const entry = store.upsertLesson({
                rule: lesson.rule,
                scope: {
                    entity: lesson.entity || first?.entity || null,
                    fields: Array.isArray(lesson.fields) ? lesson.fields : [],
                    errorClass: first?.errorClass || null,
                },
                example: first ? { before: first.failingShape, after: first.workingShape, diff: first.pair.diff } : null,
                signatureKey: first?.pair.error.signature?.key || null,
                evidence: first ? { chatId, toolCallId: first.pair.error.toolCallId } : { chatId },
                kind: "lesson",
                source: "auto",
            });
            if (entry) summary.stored.push(entry.id);
        }
        for (const c of candidates) {
            if (covered.has(c.id) || summary.stored.length >= MAX_LESSONS_PER_RUN) break;
            const entry = store.upsertLesson({
                rule: deterministicLesson(c.pair),
                scope: { entity: c.entity, fields: [], errorClass: c.errorClass },
                example: { before: c.failingShape, after: c.workingShape, diff: c.pair.diff },
                signatureKey: c.pair.error.signature?.key || null,
                evidence: { chatId, toolCallId: c.pair.error.toolCallId },
                kind: "lesson",
                source: "auto",
            });
            if (entry) summary.stored.push(entry.id);
        }
        const seen = new Set();
        for (const ev of unpaired) {
            const key = ev.signature?.key;
            if (!key || seen.has(key)) continue;
            seen.add(key);
            if (summary.stored.length + summary.pitfalls.length >= MAX_LESSONS_PER_RUN) break;
            const entry = store.upsertLesson({
                rule: pitfallRule(ev),
                scope: { entity: ev.shape?.entity || null, fields: [], errorClass: ev.signature?.class || null },
                example: null,
                signatureKey: key,
                evidence: { chatId, toolCallId: ev.toolCallId },
                kind: "pitfall",
                source: "auto",
            });
            if (entry) summary.pitfalls.push(entry.id);
        }
        return summary;
    } catch (err) {
        summary.error = err?.message || String(err);
        return summary;
    }
}

const LESSONS_BLOCK = /\n*<harness_lessons>[\s\S]*?<\/harness_lessons>/;

// Swap the lessons block inside a live system prompt for a fresh one so
// the next turn in this chat sees what the run just taught the store.
export function replaceLessonsBlock(systemPrompt, section) {
    const prompt = String(systemPrompt || "");
    const clean = section ? `\n\n${section}` : "";
    if (LESSONS_BLOCK.test(prompt)) return prompt.replace(LESSONS_BLOCK, clean);
    return clean ? prompt + clean : prompt;
}

export { classifyQueryError };
